import { useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { useNavigate } from 'react-router-dom';

interface PrivateRouteProps {
  children: React.ReactNode;
}

/**
 * TODO: Replace with something more robust in the future.
 * PrivateRoute is a very simple, basic, component to manage access to routes for the alpha release.
 * It's only reliable for checking if the user is logged in or not. Additional access controls are
 * should be handled somewhere else.
 *
 * Example usage:
 *
 *  <Routes>
 *    // Public routes
 *    <Route path="/signin" element={<SignIn />} />
 *    <Route path="/signout" element={<SignOut />} />
 *
 *    // Protected routes
 *    <Route
 *      path="/profile"
 *      element={
 *        <PrivateRoute>
 *          <Profile />
 *        </PrivateRoute>
 *      }
 *    />
 *    <Route
 *      path="/dashboard"
 *      element={
 *        <PrivateRoute>
 *          <Dashboard />
 *        </PrivateRoute>
 *      }
 *          />
 *   </Routes>
 *
 * @param children - The component to wrap.
 * @returns The wrapped component or null if the user is not logged in.
 */
export function PrivateRoute({ children }: PrivateRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const userProfile = useSelector((state: RootState) => state.map3Data.userProfile);

  const isAuthenticated = !!user;
  const hasUserProfile = !!userProfile?.uid;

  useEffect(() => {
    try {
      if (!isAuthenticated) {
        navigate('/sign-in');
        return;
      }

      // Only check hasUserProfile when we're sure the profile has finished loading
      if (!hasUserProfile) {
        navigate('/complete-signup');
        return;
      }
    } catch (error) {
      console.error('Error checking auth state:', error);
      navigate('/sign-in');
    }
  }, [navigate, isAuthenticated, hasUserProfile]);

  return isAuthenticated && hasUserProfile ? <>{children}</> : null;
}
